import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import firebaseService from '../firebase.service';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { useRedirect } from '../contexts/RedirectContext';
import { useDataCache } from '../contexts/DataCacheContext';
import { useProject } from '../contexts/ProjectContext';
import optimizedDataService from '../services/OptimizedDataService';

const BookList = () => {
  const { csvType, projectId } = useParams();
  const navigate = useNavigate();
  const { projects: contextProjects, defaultProject, isValidProject, projectsLoaded } = useProject();
  const initialProject = (projectId && isValidProject(projectId)) ? projectId : defaultProject;
  const [activeProject, setActiveProject] = useState(initialProject);
  const hasUserSelected = useRef(false);
  const hasSyncedDefault = useRef(false);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [viewMode, setViewMode] = useState('table'); // 'table' only
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  // Media types are now stored in book data during Excel processing
  const [batchExporting, setBatchExporting] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const isRefreshingRef = useRef(false);
  const isInternalRefreshRef = useRef(false);
  const { isRedirectEnabled, setRedirectEnabled, getRedirectLinks, setRedirectLinksFromData } = useRedirect();
  const dataCache = useDataCache();
  // const { t } = useTranslation(); // Unused for now

  const projects = contextProjects;

  // Sync local activeProject with Firestore default once loaded (only if user hasn't manually picked)
  useEffect(() => {
    if (projectsLoaded && !hasSyncedDefault.current && !projectId && !hasUserSelected.current) {
      setActiveProject(defaultProject);
      hasSyncedDefault.current = true;
    }
  }, [projectsLoaded, defaultProject, projectId]);

  const handleProjectChange = useCallback((e) => {
    hasUserSelected.current = true;
    setActiveProject(e.target.value);
  }, []);

  // Initialize optimized data service with cache context
  useEffect(() => {
    optimizedDataService.setCache(dataCache);
  }, [dataCache]);

  useEffect(() => {
    let isMounted = true;
    
    // Load data using optimized service
    const loadBooks = async () => {
      try {
        setLoading(true);
        console.log(`🔄 BookList: Starting to load books for project: ${activeProject}`);
        console.log(`🔄 BookList: DataService cache context:`, dataCache);
        
        // Use optimized service only
        const books = await optimizedDataService.getAllBooks(activeProject);
        
        // Check if component is still mounted before updating state
        if (!isMounted) {
          console.log('⏹️ BookList: Component unmounted, skipping state update');
          return;
        }
        
        console.log(`✅ Used optimized data service for ${activeProject}`);
        console.log(`✅ BookList: Successfully loaded ${books.length} books for ${activeProject}`);
        console.log(`📚 BookList: First book sample:`, books[0]);
        setBooks(books);
        setFilteredBooks(books);
        
        setLoading(false);
      } catch (error) {
        console.error('❌ BookList: Error loading books:', error);
        
        // Only update state if component is still mounted
        if (isMounted) {
          setBooks([]);
          setFilteredBooks([]);
          setLoading(false);
        }
      }
    };

    // Load books immediately
    loadBooks();
    
    return () => {
      isMounted = false;
    };
  }, [csvType, activeProject]);

  // Listen for storage events only (when cache is cleared in another tab)
  useEffect(() => {
    let refreshTimeout = null;
    let storageListener = null;

    const handleStorageChange = (e) => {
      if (e.key === 'bookCache' && e.newValue !== e.oldValue) {
        // Don't trigger refresh if we're already refreshing or if this is our own internal refresh
        if (isRefreshingRef.current || isInternalRefreshRef.current) {
          console.log('🔄 Already refreshing or internal refresh, ignoring storage event...');
          return;
        }
        
        console.log('🔄 Cache changed, scheduling refresh...');
        
        // Debounce refresh calls to prevent rapid successive calls
        if (refreshTimeout) {
          clearTimeout(refreshTimeout);
        }
        
        refreshTimeout = setTimeout(() => {
          // Double-check we're not already refreshing
          if (!isRefreshingRef.current && !isInternalRefreshRef.current) {
            handleRefresh();
          }
        }, 2000); // Increased to 2 seconds debounce
      }
    };

    // Only add listener if not currently refreshing
    if (!isRefreshingRef.current) {
      storageListener = handleStorageChange;
      window.addEventListener('storage', storageListener);
    }

    return () => {
      if (storageListener) {
        window.removeEventListener('storage', storageListener);
      }
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
    };
  }, [activeProject]); // Remove dataCache dependency to prevent constant re-runs

  useEffect(() => {
    // Filter books based on search term
    const filtered = books.filter(book => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (book.name_en && book.name_en.toLowerCase().includes(searchLower)) ||
        (book.name_tc && book.name_tc.toLowerCase().includes(searchLower)) ||
        (book.name_sc && book.name_sc.toLowerCase().includes(searchLower)) ||
        (book.author_en && book.author_en.toLowerCase().includes(searchLower)) ||
        (book.author_tc && book.author_tc.toLowerCase().includes(searchLower)) ||
        (book.author_sc && book.author_sc.toLowerCase().includes(searchLower)) ||
        (book.publisher_en && book.publisher_en.toLowerCase().includes(searchLower)) ||
        (book.publisher_tc && book.publisher_tc.toLowerCase().includes(searchLower)) ||
        (book.publisher_sc && book.publisher_sc.toLowerCase().includes(searchLower)) ||
        (book.code && book.code.toLowerCase().includes(searchLower))
      );
    });
    setFilteredBooks(filtered);
  }, [searchTerm, books]);

  // Media types are now detected during Excel processing and stored in book data

  const redirectEnabled = isRedirectEnabled(activeProject);
  const redirectLinks = getRedirectLinks(activeProject);

  // Load redirect links from book data when books are loaded
  useEffect(() => {
    if (books.length > 0) {
      setRedirectLinksFromData(activeProject, books);
    }
  }, [books, activeProject, setRedirectLinksFromData]);

  // Load Excel file from URL
  const loadExcelFile = async (excelUrl) => {
    try {
      const response = await fetch(excelUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // Get the first worksheet
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      // Convert to JSON with header row
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      if (jsonData.length < 2) {
        console.log('Excel file has no data rows');
        return [];
      }
      
      const headers = jsonData[0];
      const books = [];
      const bookCodes = new Set(); // Track unique book codes to prevent duplicates
      const duplicateCodes = [];
      
      console.log('Excel headers:', headers);
      
      // Find code column index
      let codeColumnIndex = headers.findIndex(header => 
        header && header.toLowerCase().includes('code')
      );
      
      if (codeColumnIndex === -1) {
        codeColumnIndex = headers.findIndex(header => 
          header && (
            header.toLowerCase().includes('book_code') ||
            header.toLowerCase().includes('bookcode') ||
            header.toLowerCase().includes('id') ||
            header.toLowerCase().includes('identifier')
          )
        );
      }
      
      console.log('Code column index:', codeColumnIndex, 'Header:', headers[codeColumnIndex]);
      
      // Process data rows (skip header row)
      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (row && row.length > 0) {
          const book = {};
          
          // Map each column to book property
          headers.forEach((header, index) => {
            if (header && row[index] !== undefined) {
              book[header.trim()] = String(row[index]).trim();
            }
          });
          
          // Set code from identified column
          if (codeColumnIndex !== -1 && row[codeColumnIndex]) {
            book.code = String(row[codeColumnIndex]).trim();
          }
          
          // Only add books that have a name or valid code
          if (book.name_en || book.name_tc || book.name_sc || 
              (book.code && /^[A-Z0-9]+-[A-Z0-9]+-[A-Z0-9]+-[A-Z0-9]+$/.test(book.code))) {
            
            // Check for duplicates
            if (book.code && bookCodes.has(book.code)) {
              duplicateCodes.push(book.code);
              console.log(`DUPLICATE FOUND: ${book.code} at row ${i + 1}`);
            } else if (book.code) {
              bookCodes.add(book.code);
            }
            
            books.push(book);
          }
        }
      }
      
      console.log(`Excel parsing summary: ${books.length} books, ${bookCodes.size} unique codes, ${duplicateCodes.length} duplicates`);
      if (duplicateCodes.length > 0) {
        console.log('Duplicate codes:', duplicateCodes);
      }
      
      return books;
    } catch (error) {
      console.error('Error loading Excel file:', error);
      throw error;
    }
  };

  // Load CSV file from URL
  const loadCSVFile = async (csvUrl) => {
    try {
      const response = await fetch(csvUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const csvText = await response.text();
      return parseCSVWithDuplicates(csvText);
    } catch (error) {
      console.error('Error loading CSV file:', error);
      throw error;
    }
  };

  // Enhanced CSV parser with duplicate detection
  const parseCSVWithDuplicates = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    const books = [];
    const bookCodes = new Set(); // Track unique book codes to prevent duplicates
    const duplicateCodes = [];

    console.log('Total lines in CSV:', lines.length);
    
    // Find header row
    let headerRowIndex = 0;
    let headers = [];
    let codeColumnIndex = -1;
    
    for (let i = 0; i < Math.min(5, lines.length); i++) {
      const potentialHeaders = parseCSVLine(lines[i]);
      
      const hasCodeColumn = potentialHeaders.some(header => 
        header && (
          header.toLowerCase().includes('code') ||
          header.toLowerCase().includes('book_code') ||
          header.toLowerCase().includes('bookcode') ||
          header.toLowerCase().includes('id') ||
          header.toLowerCase().includes('identifier')
        )
      );
      
      const hasNameColumn = potentialHeaders.some(header => 
        header && (
          header.toLowerCase().includes('name') ||
          header.toLowerCase().includes('title') ||
          header.toLowerCase().includes('book_name')
        )
      );
      
      if (hasCodeColumn && hasNameColumn) {
        headerRowIndex = i;
        headers = potentialHeaders;
        break;
      }
    }
    
    if (headers.length === 0) {
      headers = parseCSVLine(lines[0]);
    }
    
    // Find code column
    codeColumnIndex = headers.findIndex(header => 
      header && header.toLowerCase().includes('code')
    );
    
    if (codeColumnIndex === -1) {
      codeColumnIndex = headers.findIndex(header => 
        header && (
          header.toLowerCase().includes('book_code') ||
          header.toLowerCase().includes('bookcode') ||
          header.toLowerCase().includes('id') ||
          header.toLowerCase().includes('identifier')
        )
      );
    }

    // Process data rows (skip header row)
    for (let i = headerRowIndex + 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line) {
        const values = parseCSVLine(line);
        const book = {};
        headers.forEach((header, index) => {
          book[header.trim()] = values[index] ? values[index].trim() : '';
        });
        
        if (codeColumnIndex !== -1 && values[codeColumnIndex]) {
          book.code = values[codeColumnIndex].trim();
        }
        
        if (book.name_en || book.name_tc || book.name_sc || 
            (book.code && /^[A-Z0-9]+-[A-Z0-9]+-[A-Z0-9]+-[A-Z0-9]+$/.test(book.code))) {
          
          // Check for duplicates
          if (book.code && bookCodes.has(book.code)) {
            duplicateCodes.push(book.code);
            console.log(`DUPLICATE FOUND: ${book.code} at line ${i + 1}`);
          } else if (book.code) {
            bookCodes.add(book.code);
          }
          
          books.push(book);
        }
      }
    }
    
    console.log(`CSV parsing summary: ${books.length} books, ${bookCodes.size} unique codes, ${duplicateCodes.length} duplicates`);
    if (duplicateCodes.length > 0) {
      console.log('Duplicate codes:', duplicateCodes);
    }
    
    return books;
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort the filtered books
  const getSortedBooks = () => {
    if (!sortConfig.key) return filteredBooks;

    return [...filteredBooks].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';

      // Handle special cases
      if (sortConfig.key === 'mediaType') {
        aValue = a.mediaType || 'none';
        bValue = b.mediaType || 'none';
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Media types are now detected during Excel processing and stored in book data

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    const headers = lines[0].split(',');
    const books = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line) {
        const values = parseCSVLine(line);
        if (values.length >= headers.length) {
          const book = {};
          headers.forEach((header, index) => {
            book[header.trim()] = values[index] ? values[index].trim() : '';
          });
          // Only add books that have a name
          if (book.name_en || book.name_tc || book.name_sc) {
            books.push(book);
          }
        }
      }
    }
    return books;
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  const handleBookClick = (book) => {
    if (book.code) {
      // Open infobox in a new tab to prevent page freezing
      const projectPath = `/${activeProject}`;
      const infoboxUrl = `${window.location.origin}${projectPath}/book/${book.code}`;
      window.open(infoboxUrl, '_blank');
      console.log(`🔗 Opening infobox in new tab: ${infoboxUrl}`);
    }
  };

  const handleViewBook = (book) => {
    if (book.code) {
      // Open infobox in a new tab to prevent page freezing
      const projectPath = `/${activeProject}`;
      const infoboxUrl = `${window.location.origin}${projectPath}/book/${book.code}`;
      window.open(infoboxUrl, '_blank');
      console.log(`🔗 Opening infobox in new tab: ${infoboxUrl}`);
    }
  };

  const handleGenerateQR = (book) => {
    if (book.code) {
      generateQRCode(book.code);
    }
  };

  const handleRefresh = async () => {
    // Prevent multiple simultaneous refresh calls using ref
    if (isRefreshingRef.current) {
      console.log('🔄 Refresh already in progress, skipping...');
      return;
    }

    isRefreshingRef.current = true;
    isInternalRefreshRef.current = true;
    setRefreshing(true);
    try {
      // Clear cache and reload data
      dataCache.clearCache(activeProject, 'books_list');
      dataCache.clearCache(activeProject, 'books_detail');
      dataCache.clearCache(activeProject, 'books'); // Also clear legacy cache
      console.log(`🗑️ Cleared cache for ${activeProject} books`);
      
      const books = await optimizedDataService.getAllBooks(activeProject);
      console.log(`✅ Refreshed: loaded ${books.length} books for ${activeProject}`);
      setBooks(books);
      setFilteredBooks(books);
    } catch (error) {
      console.error('Error refreshing books:', error);
    } finally {
      setRefreshing(false);
      // Add a small delay before allowing new refresh operations
      setTimeout(() => {
        isRefreshingRef.current = false;
        isInternalRefreshRef.current = false;
      }, 1000); // Increased delay to 1 second
    }
  };

  const handleWebsiteClick = (website, e) => {
    e.stopPropagation();
    if (website && website.startsWith('http')) {
      window.open(website, '_blank');
    }
  };


  // Generate QR code for individual book link
  const generateQRCode = (bookCode) => {
    const baseUrl = window.location.origin;
    // Use project-specific URL structure to match the working infobox URLs
    const projectPath = `/${activeProject}`;
    const bookUrl = `${baseUrl}${projectPath}/book/${bookCode}`;
    
    // Create QR code URL with higher resolution and format
    // Size: 300x300 for QR code, format: JPG
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&format=jpg&data=${encodeURIComponent(bookUrl)}`;
    
    // Create a new window with canvas to combine QR code and text
    const qrWindow = window.open('', '_blank', 'width=500,height=600');
    
    qrWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Code - ${bookCode}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            margin: 0;
            background-color: #f5f5f5;
            padding: 20px;
            box-sizing: border-box;
          }
          .container {
            background: white;
            padding: 30px;
            border-radius: 10px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            text-align: center;
            max-width: 400px;
          }
          canvas {
            border: 2px solid #ddd;
            border-radius: 8px;
            margin-bottom: 20px;
          }
          .download-btn {
            margin-top: 20px;
            padding: 10px 20px;
            background-color: #007bff;
            color: white;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            font-size: 14px;
            text-decoration: none;
            display: inline-block;
          }
          .download-btn:hover {
            background-color: #0056b3;
          }
          .info {
            margin-top: 15px;
            font-size: 12px;
            color: #666;
            line-height: 1.4;
          }
          .loading {
            color: #666;
            font-style: italic;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="loading">Generating QR code with book code...</div>
          <canvas id="qrCanvas" width="350" height="400" style="display: none;"></canvas>
          <div id="downloadSection" style="display: none;">
            <a id="downloadBtn" class="download-btn">Download Combined Image</a>
            <div class="info">
              <p>This image contains both the QR code and book code</p>
              <p>URL: ${bookUrl}</p>
            </div>
          </div>
        </div>
        
        <script>
          const canvas = document.getElementById('qrCanvas');
          const ctx = canvas.getContext('2d');
          const downloadSection = document.getElementById('downloadSection');
          const downloadBtn = document.getElementById('downloadBtn');
          const loading = document.querySelector('.loading');
          
          // Set canvas background to white
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Load QR code image
          const qrImg = new Image();
          qrImg.crossOrigin = 'anonymous';
          qrImg.onload = function() {
            // Draw QR code (centered, 300x300)
            const qrSize = 300;
            const qrX = (canvas.width - qrSize) / 2;
            const qrY = 20;
            ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
            
            // Draw book code text below QR code (40% larger)
            ctx.fillStyle = 'black';
            ctx.font = 'bold 34px Arial'; // Increased from 24px to 34px (40% larger)
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            
            const textY = qrY + qrSize + 40;
            ctx.fillText('${bookCode}', canvas.width / 2, textY);
            
            // Hide loading and show download section
            loading.style.display = 'none';
            canvas.style.display = 'block';
            downloadSection.style.display = 'block';
            
            // Set up download functionality
            downloadBtn.onclick = function() {
              const link = document.createElement('a');
              link.download = 'QR_${bookCode}.jpg';
              link.href = canvas.toDataURL('image/jpeg', 0.9);
              link.click();
            };
          };
          
          qrImg.onerror = function() {
            loading.textContent = 'Error loading QR code. Please try again.';
            loading.style.color = 'red';
          };
          
          qrImg.src = '${qrCodeUrl}';
        </script>
      </body>
      </html>
    `);
    
    qrWindow.document.close();
  };

  // Batch export QR codes for all books
  const handleBatchQRExport = async () => {
    setBatchExporting(true);
    setBatchProgress(0);

    try {
      // Fetch ALL books for the current project (not just displayed books)
      // Check if cache has empty data and clear it if so
      const cachedData = dataCache.getCachedData(activeProject, 'books_list');
      if (Array.isArray(cachedData) && cachedData.length === 0) {
        dataCache.clearCache(activeProject, 'books_list');
      }
      
      let allProjectBooks = [];
      try {
        allProjectBooks = await optimizedDataService.getAllBooks(activeProject);
      } catch (serviceError) {
        console.error(`❌ Batch Export: Service call failed:`, serviceError);
        throw serviceError;
      }
      
      if (!allProjectBooks || allProjectBooks.length === 0) {
        alert(`No books available for export in ${activeProject.toUpperCase()} project.\n\nPlease ensure you have uploaded data for this project through the Admin Panel.`);
        setBatchExporting(false);
        return;
      }

      const zip = new JSZip();
      const baseUrl = window.location.origin;
      let completed = 0;
      let validBooks = 0;
      
      // Count valid books first
      const validBooksList = allProjectBooks.filter(book => {
        const hasCode = book.code && typeof book.code === 'string';
        const hasLength = book.code && book.code.length >= 5;
        const hasHyphen = book.code && book.code.includes('-');
        
        return hasCode && hasLength && hasHyphen;
      });
      
      validBooks = validBooksList.length;
      
      if (validBooks === 0) {
        alert('No valid book codes found for export. Please check your CSV data.');
        return;
      }

      // Process books in batches to avoid overwhelming the browser
      const batchSize = 10;
      const totalBatches = Math.ceil(validBooks / batchSize);

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const startIndex = batchIndex * batchSize;
        const endIndex = Math.min(startIndex + batchSize, validBooks);
        const batch = validBooksList.slice(startIndex, endIndex);

        // Process current batch
        const batchPromises = batch.map(async (book) => {
          // Validate that we have a proper book code
          if (!book.code || typeof book.code !== 'string' || book.code.length < 5 || !book.code.includes('-')) {
            console.log(`Skipping book with invalid code:`, book.code, book);
            return null;
          }

          try {
            // Use project-specific URL structure to match the working infobox URLs
            const projectPath = `/${activeProject}`;
            const bookUrl = `${baseUrl}${projectPath}/book/${book.code}`;
            
            const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&format=jpg&data=${encodeURIComponent(bookUrl)}`;
            
            // Fetch QR code image
            const response = await fetch(qrCodeUrl);
            if (!response.ok) throw new Error(`Failed to fetch QR code for ${book.code}`);
            
            const qrBlob = await response.blob();
            
            // Create canvas to combine QR code with book code text
            const canvas = document.createElement('canvas');
            canvas.width = 350;
            canvas.height = 400;
            const ctx = canvas.getContext('2d');
            
            // Set white background
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Load and draw QR code
            const qrImg = new Image();
            qrImg.crossOrigin = 'anonymous';
            
            return new Promise((resolve) => {
              qrImg.onload = () => {
                // Draw QR code (centered, 300x300)
                const qrSize = 300;
                const qrX = (canvas.width - qrSize) / 2;
                const qrY = 20;
                ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
                
                // Draw book code text below QR code
                ctx.fillStyle = 'black';
                ctx.font = 'bold 34px Arial';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                
                const textY = qrY + qrSize + 40;
                ctx.fillText(book.code, canvas.width / 2, textY);
                
                // Convert canvas to blob
                canvas.toBlob((blob) => {
                  if (blob) {
                    zip.file(`QR_${book.code}.jpg`, blob);
                  }
                  resolve();
                }, 'image/jpeg', 0.9);
              };
              
              qrImg.onerror = () => {
                console.error(`Failed to load QR code for ${book.code}`);
                resolve();
              };
              
              qrImg.src = URL.createObjectURL(qrBlob);
            });
          } catch (error) {
            console.error(`Error processing ${book.code}:`, error);
            return null;
          }
        });

        // Wait for current batch to complete
        await Promise.all(batchPromises);
        
        completed += batch.length;
        setBatchProgress(Math.round((completed / validBooks) * 100));
        
        // Small delay between batches to prevent browser freezing
        if (batchIndex < totalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Generate and download ZIP file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Create download link
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `QR_Codes_${activeProject.toUpperCase()}_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(link.href);
      
      alert(`Successfully exported ${validBooks} QR codes!`);
      
    } catch (error) {
      console.error('Error during batch export:', error);
      alert('Error during batch export. Please try again.');
    } finally {
      setBatchExporting(false);
      setBatchProgress(0);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-900">
        <div className="text-lg text-white">Loading books...</div>
      </div>
    );
  }

  const getProjectPath = (path) => {
    return `/${activeProject}${path}`;
  };

  return (
    <>
      <nav className="bg-gray-900 shadow-lg border-b border-gray-700">
        <div className="container mx-auto px-4 py-3">
          {/* Mobile Navigation */}
          <div className="md:hidden">
            {/* Logo Row - Centered */}
            <div className="flex justify-center mb-3">
              <img src="/virtrix-logo.png" alt="VIRTRIX Logo" className="h-8 w-auto" />
            </div>
            
            {/* Title and Navigation Row */}
            <div className="flex items-center justify-between">
              <h1 className="text-lg font-bold text-white">HK Pavilion CMS</h1>
              <div className="flex space-x-1">
                <a href={getProjectPath("/books/booklist")} className="text-white font-semibold bg-blue-600 px-2 py-1 rounded text-xs">Book List</a>
                <a href={getProjectPath("/companies")} className="text-gray-300 hover:text-white text-xs px-2 py-1 rounded border border-gray-600">Companies</a>
                <a href={getProjectPath("/zones")} className="text-gray-300 hover:text-white text-xs px-2 py-1 rounded border border-gray-600">Zone QR</a>
                <a href="/admin" className="text-gray-300 hover:text-white text-xs px-2 py-1 rounded border border-gray-600">Admin</a>
              </div>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:block">
            {/* Logo Row - Centered */}
            <div className="flex justify-center mb-4">
              <img src="/virtrix-logo.png" alt="VIRTRIX Logo" className="h-10 w-auto" />
            </div>
            
            {/* Title and Controls Row */}
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <h1 className="text-2xl font-bold text-white">HK Pavilion CMS</h1>
                <div className="relative">
                  <select
                    value={activeProject}
                    onChange={handleProjectChange}
                    className="bg-gray-800 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                    refreshing
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {refreshing ? 'Refreshing...' : '🔄 Refresh'}
                </button>
              </div>
              <div className="space-x-4">
                <a href={getProjectPath("/books/booklist")} className="text-white font-semibold bg-blue-600 px-3 py-1 rounded">Book List</a>
                <a href={getProjectPath("/companies")} className="text-gray-300 hover:text-white">Companies</a>
                <a href={getProjectPath("/zones")} className="text-gray-300 hover:text-white">Zone QR</a>
                <a href="/admin" className="text-gray-300 hover:text-white">Admin</a>
              </div>
            </div>
          </div>
        </div>
      </nav>
      <div className="min-h-screen bg-gray-900">
        <div className="w-full px-4 py-2 md:py-4">
          <div className="mb-4 bg-transparent">
            {/* Mobile Controls and Search Row */}
            <div className="md:hidden flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <select
                    value={activeProject}
                    onChange={handleProjectChange}
                    className="bg-gray-800 text-white border border-gray-600 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 outline outline-2 outline-blue-600"
                  >
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className={`px-2 pb-2 text-lg transition-colors flex items-center justify-center ${
                    refreshing
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-blue-400 hover:text-blue-300'
                  }`}
                >
                  {refreshing ? '⏳' : '🔄'}
                </button>
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search books..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-48 px-3 py-2 bg-gray-900 text-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400 text-sm outline outline-2 outline-blue-600"
                />
              </div>
            </div>
            
            {/* Desktop Title and Search */}
            <div className="hidden md:block">
              <h1 className="text-xl md:text-2xl font-bold mb-2 md:mb-3 text-white">
                {projects.find(p => p.id === activeProject)?.name} - {csvType === 'booklist' ? 'Book List' : 'Book Collection'}
              </h1>
            </div>
            {/* Batch Export Section */}
            <div className="mt-3">
              {/* Desktop Layout - Controls on the right */}
              <div className="hidden md:flex justify-between items-center">
                <p className="text-gray-200 text-base font-medium">
                  Showing {filteredBooks.length} of {books.length} books
                </p>
                
                <div className="flex items-center space-x-4">
                  {/* Desktop Search Bar */}
                  <div className="relative w-96">
                    <input
                      type="text"
                      placeholder="Search books..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400 text-sm"
                    />
                  </div>
                  {batchExporting && (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-blue-400 text-sm font-medium">Exporting... {batchProgress}%</span>
                    </div>
                  )}
                  
                  {/* Redirect Toggle - Desktop */}
                  <div className="flex items-center justify-between bg-gray-700 px-4 py-1.5 rounded-lg border border-gray-500 w-56">
                    <span className="text-white text-sm font-medium">Redirect QR Codes:</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={redirectEnabled}
                        onChange={(e) => setRedirectEnabled(activeProject, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-400 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                    </label>
                  </div>
                  
                  <button
                    onClick={handleBatchQRExport}
                    disabled={batchExporting || books.length === 0}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm w-44 ${
                      batchExporting || books.length === 0
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {batchExporting ? 'Exporting...' : 'Export QR Codes'}
                  </button>
                </div>
              </div>
              
              {/* Mobile Layout - Controls on the right */}
              <div className="md:hidden flex justify-between items-center">
                <p className="text-gray-200 text-sm font-medium">
                  Showing {filteredBooks.length} of {books.length} books
                </p>
                
                <div className="flex items-center space-x-2">
                  {batchExporting && (
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-blue-400 text-xs font-medium">Exporting... {batchProgress}%</span>
                    </div>
                  )}
                  
                  {/* Redirect Toggle - Mobile optimized (30% smaller) */}
                  <div className="flex items-center justify-between p-2">
                    <span className="text-white text-xs font-medium mr-3">Redirect QR:</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={redirectEnabled}
                        onChange={(e) => setRedirectEnabled(activeProject, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-8 h-4 bg-gray-400 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-green-500"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            {books.length === 0 && (
              <p className="text-yellow-400 mt-2">
                No books uploaded yet for {projects.find(p => p.id === activeProject)?.name || activeProject.toUpperCase()}. Use the Admin panel to upload book data.
              </p>
            )}
          </div>

            <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden mt-1 md:mt-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm md:text-base">
                  <thead className="bg-gray-700">
                    <tr>
                      <th 
                        className="px-1 md:px-3 py-2 md:py-4 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors"
                        style={{width: '80px', minWidth: '80px'}}
                        onClick={() => handleSort('code')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Code</span>
                          {sortConfig.key === 'code' && (
                            <span className="text-blue-400">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-1 md:px-3 py-2 md:py-4 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors"
                        style={{width: '60px', minWidth: '60px'}}
                        onClick={() => handleSort('mediaType')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Media</span>
                          {sortConfig.key === 'mediaType' && (
                            <span className="text-blue-400">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-1 md:px-3 py-2 md:py-4 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors"
                        style={{width: '120px', minWidth: '120px'}}
                        onClick={() => handleSort('name_en')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Name (EN)</span>
                          {sortConfig.key === 'name_en' && (
                            <span className="text-blue-400">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-1 md:px-3 py-2 md:py-4 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors"
                        style={{width: '100px', minWidth: '100px'}}
                        onClick={() => handleSort('name_tc')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Name (TC)</span>
                          {sortConfig.key === 'name_tc' && (
                            <span className="text-blue-400">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-1 md:px-3 py-2 md:py-4 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors"
                        style={{width: '60px', minWidth: '60px'}}
                        onClick={() => handleSort('publisher_en')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Publisher</span>
                          {sortConfig.key === 'publisher_en' && (
                            <span className="text-blue-400">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-1 md:px-3 py-2 md:py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                        style={{width: '200px', minWidth: '200px'}}
                      >
                        Redirect
                      </th>
                      <th 
                        className="px-1 md:px-3 py-2 md:py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                        style={{width: '180px', minWidth: '180px'}}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-800 divide-y divide-gray-700">
                    {getSortedBooks().map((book, index) => (
                      <tr key={index} className="hover:bg-gray-700 transition-colors duration-200">
                        <td className="px-1 md:px-3 py-2 md:py-4" style={{width: '80px', minWidth: '80px'}}>
                          <div className="text-xs md:text-sm font-semibold text-white">
                            {book.code || '-'}
                          </div>
                        </td>
                        <td className="px-1 md:px-3 py-2 md:py-4" style={{width: '60px', minWidth: '60px'}}>
                          <div className="text-xs">
                            <span className={`px-1 py-0.5 rounded-full text-xs font-medium ${
                              book.mediaType === 'glb' 
                                ? 'bg-purple-600 text-white' 
                                : book.mediaType === 'image' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-gray-600 text-gray-300'
                            }`}>
                              {book.mediaType === 'glb' ? '3D' : 
                               book.mediaType === 'image' ? 'Photo' : 
                               book.mediaType || 'Unknown'}
                            </span>
                          </div>
                        </td>
                        <td className="px-1 md:px-3 py-2 md:py-4" style={{width: '120px', minWidth: '120px'}}>
                          <div className="text-xs md:text-sm text-gray-100" title={book.name_en || '-'}>
                            {book.name_en || '-'}
                          </div>
                        </td>
                        <td className="px-1 md:px-3 py-2 md:py-4" style={{width: '100px', minWidth: '100px'}}>
                          <div className="text-xs md:text-sm text-gray-100" title={book.name_tc || '-'}>
                            {book.name_tc || '-'}
                          </div>
                        </td>
                        <td className="px-1 md:px-3 py-2 md:py-4" style={{width: '60px', minWidth: '60px'}}>
                          <div className="text-xs md:text-sm text-gray-200" title={book.publisher_en || book.publisher_tc || book.publisher_sc || '-'}>
                            {book.publisher_en || book.publisher_tc || book.publisher_sc || '-'}
                          </div>
                        </td>
                        <td className="px-1 md:px-3 py-2 md:py-4" style={{width: '200px', minWidth: '200px'}}>
                          <div className="text-xs md:text-sm text-gray-200" title={redirectLinks[book.code] || '-'}>
                            {redirectLinks[book.code] ? (
                              <>
                                {/* Mobile: Show "Link" text */}
                                <span className="md:hidden text-blue-400 hover:text-blue-300 underline transition-colors text-xs">
                                  <a
                                    href={redirectLinks[book.code]}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Link
                                  </a>
                                </span>
                                {/* Desktop: Show full URL */}
                                <a
                                  href={redirectLinks[book.code]}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hidden md:inline text-blue-400 hover:text-blue-300 underline transition-colors text-xs break-all"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {redirectLinks[book.code]}
                                </a>
                              </>
                            ) : (
                              '-'
                            )}
                          </div>
                        </td>
                        <td className="px-1 md:px-3 py-2 md:py-4 text-xs md:text-sm font-medium" style={{width: '180px', minWidth: '180px'}}>
                          <div className="flex items-center gap-1 md:gap-1.5 flex-wrap">
                            {book.code && (
                              <button
                                onClick={() => handleBookClick(book)}
                                className="px-2.5 py-1 md:px-3 md:py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-xs md:text-sm font-medium transition-colors whitespace-nowrap"
                              >
                                View
                              </button>
                            )}
                            {book.code && (
                              <div className="inline-flex rounded overflow-hidden border border-gray-600">
                                <button
                                  onClick={() => window.open(`${window.location.origin}/${activeProject}/book/${book.code}?lang=en`, '_blank')}
                                  className="px-2 py-1 md:px-2.5 md:py-1.5 bg-gray-700 hover:bg-yellow-600 text-yellow-300 hover:text-white text-xs md:text-sm font-medium transition-colors"
                                >
                                  EN
                                </button>
                                <button
                                  onClick={() => window.open(`${window.location.origin}/${activeProject}/book/${book.code}?lang=tc`, '_blank')}
                                  className="px-2 py-1 md:px-2.5 md:py-1.5 bg-gray-700 hover:bg-orange-600 text-orange-300 hover:text-white text-xs md:text-sm font-medium transition-colors border-l border-gray-600"
                                >
                                  TC
                                </button>
                                <button
                                  onClick={() => window.open(`${window.location.origin}/${activeProject}/book/${book.code}?lang=sc`, '_blank')}
                                  className="px-2 py-1 md:px-2.5 md:py-1.5 bg-gray-700 hover:bg-pink-600 text-pink-300 hover:text-white text-xs md:text-sm font-medium transition-colors border-l border-gray-600"
                                >
                                  SC
                                </button>
                              </div>
                            )}
                            {book.code && (
                              <button
                                onClick={() => generateQRCode(book.code)}
                                className="px-2.5 py-1 md:px-3 md:py-1.5 rounded bg-green-700 hover:bg-green-600 text-green-200 hover:text-white text-xs md:text-sm font-medium transition-colors whitespace-nowrap"
                              >
                                QR
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

          {filteredBooks.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-300 text-lg font-medium">
                {books.length === 0
                  ? `No books uploaded yet for ${projects.find(p => p.id === activeProject)?.name || activeProject.toUpperCase()}. Use the Admin panel to upload book data.`
                  : 'No books found matching your search.'
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BookList;
